import React, { useEffect, useState } from 'react';
import { styled } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import DoneIcon from '@mui/icons-material/Done';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Custom styling for FormControl and Select components
const CustomFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'labelColor',
})(({ theme, disabled, labelColor }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        height: 'auto',
        '& fieldset': {
            borderRadius: '8px',
            borderColor: disabled ? 'lightgray' : 'gray',
        },
        '&:hover fieldset': {
            borderColor: disabled ? 'lightgray' : 'gray',
        },
        '&.Mui-focused fieldset': {
            borderColor: disabled ? 'lightgray' : 'gray',
        },
        padding: '8px 10px',
    },
    '& .MuiSelect-select': {
        whiteSpace: 'normal !important',
        wordBreak: 'break-word',
        paddingRight: '32px !important',
    },
    '& .MuiInputLabel-root': {
        color: disabled ? 'black' : 'gray',        
        whiteSpace: 'break-spaces',
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%) scale(1)',
        transformOrigin: 'left',
        transition: 'all 0.2s ease-in-out',
        lineHeight: '1.5',
        width: '100%',
        overflowWrap: 'break-word',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'gray',
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-shrink': {
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-root.Mui-disabled': {
        color: labelColor ? labelColor : 'gray',
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 10px',
    },
}));

const MultipleSelect = ({
    label,
    name,
    required,
    options = [],
    mappingKey = 'option',
    formik,
    className,
    maxSelect,
    onValueChange,
    disabled,
    readOnly,
    selectedValues = [],
    noSort = false,
    labelColor
}) => {
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isOpen, setIsOpen] = useState(false);
    
    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    const handleSearchChange = (event) => {
        const searchValue = event.target.value.toLowerCase();
        setSearchText(searchValue);
        
        const filtered = options.filter(option => {
            const optionText = (option[mappingKey] || option?.option || '').toString().toLowerCase();
            return optionText.includes(searchValue);
        });
        
        setFilteredOptions(filtered);
    };

    const calculatePaddingTop = (label) => {
        const lineCount = label.split('\n').length;
        let basePadding;

        if (label.length >= 800) {
            basePadding = 190;
        } else if (label.length >= 500) {
            basePadding = 130;
        } else if (label.length >= 350) {
            basePadding = 60;
        } else if (label.length >= 200) {
            basePadding = 40;
        } else if (label.length >= 100) {
            basePadding = 20;
        } else {
            basePadding = 10;
        }

        const extraPadding = (lineCount - 1) * 15;
        return `${basePadding + extraPadding}px`;
    };

    const paddingTop = calculatePaddingTop(label);
    const values = Array?.isArray(formik?.values?.[name]) ? formik?.values[name] : selectedValues;

    const handleSelectChange = (event) => {
        const {
            target: { value },
        } = event;

        const valueArray = typeof value === 'string' ? value.split(',') : value;
        if (maxSelect) {
            if (value.length <= maxSelect) {
                if (formik) {
                    formik.setFieldValue(name, valueArray);
                } else {
                    onValueChange(name, valueArray);
                }
            }
        } else {
            if (formik) {
                formik.setFieldValue(name, valueArray);
            } else {
                onValueChange(name, valueArray);
            }
        }
    };

    return (
        <CustomFormControl
            disabled={disabled}
            labelColor={labelColor}
            className={className}
            variant="outlined">
            <InputLabel sx={{ color: 'green' }} disabled={disabled}>{label}</InputLabel>
            <Select
                aria-readonly={readOnly}
                label={label}
                required={required}
                name={name}
                multiple
                disabled={disabled}
                value={values}
                onChange={handleSelectChange}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                open={isOpen}
                renderValue={(selected) => {
                    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                        return 'Select options';
                    }
                    const items = Array.isArray(selected) ? selected : [selected];
                    return (
                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'inline' }}>
                            {items.join(', ')}
                        </span>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                            width: 'auto',
                        },
                    },
                }}
                style={{ borderRadius: '8px', borderColor: 'gray', paddingTop: paddingTop }}
            >
                <div style={{ padding: '8px 16px' }}>
                    <TextField
                        fullWidth
                        autoFocus
                        placeholder="Search..."
                        value={searchText}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </div>
                
                {filteredOptions && filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                        const isDistrictField = name?.includes('DRQ-00077');
                        const itemValue = isDistrictField && option?.value
                            ? `${option.value}-${option.option}`
                            : option[mappingKey] || option?.option;
                        const isSelected =
                            values.includes(itemValue) ||
                            (isDistrictField && values.includes(option?.option));

                        return (
                            <MenuItem
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                key={index + 1}
                                value={itemValue}
                            >
                                <span>{itemValue}</span>
                                {isSelected && <DoneIcon className="text-[#0000ff99]" />}
                            </MenuItem>
                        );
                    })
                ) : (
                    <MenuItem disabled value="">
                        No options found
                    </MenuItem>
                )}
            </Select>
        </CustomFormControl>
    );
};

export default MultipleSelect;